import React, { useState } from 'react';
import { Modal, Button, Form, Alert, Spinner } from 'react-bootstrap';

interface CreateTeamModalProps {
    isOpen: boolean;
    onClose: () => void;
    onTeamCreated: (teamData: { org_id: string; name: string }) => void;
}

const CreateTeamModal: React.FC<CreateTeamModalProps> = ({
    isOpen,
    onClose,
    onTeamCreated
}) => {
    const [teamName, setTeamName] = useState('');
    const [isCreating, setIsCreating] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        if (!teamName.trim()) {
            setError('Team name is required');
            return;
        }

        setIsCreating(true);
        setError(null);

        try {
            const { createOrg } = await import('../utils/subscription');
            const result = await createOrg(teamName.trim());
            onTeamCreated(result);
            setTeamName('');
            onClose();
        } catch (error: any) {
            console.error('Error creating team:', error);
            if (error.message) {
                setError(error.message);
            } else {
                setError('Failed to create team. Please try again.');
            }
        } finally {
            setIsCreating(false);
        }
    };

    const handleClose = () => {
        if (!isCreating) {
            setTeamName('');
            setError(null);
            onClose();
        }
    };

    return (
        <Modal show={isOpen} onHide={handleClose} centered>
            <Modal.Header closeButton>
                <Modal.Title>Create Team</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {error && (
                    <Alert variant="danger" className="mb-3">
                        {error}
                    </Alert>
                )}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Team Name</Form.Label>
                        <Form.Control
                            type="text"
                            value={teamName}
                            onChange={(e) => setTeamName(e.target.value)}
                            placeholder="Enter team name"
                            disabled={isCreating}
                            autoFocus
                        />
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button
                    variant="secondary"
                    onClick={handleClose}
                    disabled={isCreating}
                >
                    Cancel
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={isCreating || !teamName.trim()}
                >
                    {isCreating ? (
                        <>
                            <Spinner animation="border" size="sm" className="me-2" />
                            Creating...
                        </>
                    ) : (
                        'Create Team'
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default CreateTeamModal; 